import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import servicenow_edit_roles from "./images/servicenow_edit_roles.png";
import servicenow_generate_password from "./images/servicenow_generate_password.png";
import servicenow_add_roles from "./images/servicenow_itsm_add_roles.png";
import servicenow_new_user from "./images/servicenow_new_user.png";
import servicenow_user_save_password from "./images/servicenow_user_save_password.png";
import servicenow_user_search from "./images/servicenow_user_search.png";
import servicenow_user_set_password from "./images/servicenow_user_set_password.png";
import servicenow_user_update from "./images/servicenow_user_update.png";
import servicenow_users_page from "./images/servicenow_users_page.png";
import ServiceNowConnectForm from "./ServiceNowConnectForm";

export const SERVICENOW_CONSOLE_URL = "https://developer.servicenow.com/";

const ServiceNowIntegration = () => {
  const [searchParams] = useSearchParams();
  const colors = useColors();
  const guideMode = searchParams.has("viewonly");
  const queryClient = useQueryClient();

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("servicenow");
  const [loading, setLoading] = useState(false);
  const [lastAttemptedValues, setLastAttemptedValues] = useState(null);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  const { registerServiceNowIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    setLastAttemptedValues(values);
    registerServiceNowIntegration(
      values.instance_url,
      values.username,
      values.password
    )
      .then((response) => {
        setLastAttemptedValues(null);
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("ServiceNow integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated
            ? "Successfully integrated with ServiceNow"
            : "Integrate with ServiceNow"
        }
        image={logos.ServiceNowLogo}
        name={"ServiceNow"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <>
              {/* Config page will be implemented in future */}
              <Typography variant="body1">
                Configuration options will be available in a future update.
              </Typography>
            </>
          ) : (
            <Box width={"740px"} mr="10px">
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                {guideMode && (
                  <Box sx={{ mb: 2, width: "100%" }}>
                    <IntegrationGuideAlert />
                  </Box>
                )}
                <Box display="flex" gap={2}>
                  <SecondaryButton onClick={handleExpandAll}>
                    Expand All Screenshots
                  </SecondaryButton>
                  <SecondaryButton onClick={handleCollapseAll}>
                    Collapse All Screenshots
                  </SecondaryButton>
                </Box>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Access ServiceNow Instance">
                  <GuidelineInstruction text="Log in to your ServiceNow instance - https://your-instance.service-now.com" />
                </GuidelineStep>
                <GuidelineStep title="Create ServiceNow User">
                  <Typography variant={"body2"} sx={{ mb: 2 }}>
                    Create a dedicated user for the Breeze integration with the
                    necessary permissions.
                  </Typography>

                  <GuidelineInstruction text="Navigate to User Administration > Users in your ServiceNow instance" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_users_page,
                        altext: "Users page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="Click 'New' to create a new user" />
                  <GuidelineInstruction text="Fill in the required fields:" />
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      • <strong>User ID:</strong> BreezeSecurity (no spaces)
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      • <strong>First name:</strong> Breeze
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      • <strong>Last name:</strong> Security
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      • <strong>Identity type:</strong> Machine (will show if
                      using the latest release)
                    </Typography>
                  </Box>
                  <GuidelineInstruction text="Check the following boxes:" />
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      • ✓ <strong>Active</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      • ✓ <strong>Web service access only</strong>
                    </Typography>
                  </Box>
                  <GuidelineInstruction text="Click 'Submit' to create the user" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_new_user,
                        altext: "New user page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Assign Required Roles">
                  <Typography variant={"body2"} sx={{ mb: 2 }}>
                    Add the necessary roles to enable the user to create and
                    manage incidents via API.
                  </Typography>

                  <GuidelineInstruction text="Find the user in the users list and click on it" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_user_search,
                        altext: "User search page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="In the user's page, scroll down to the 'Roles' related list" />
                  <GuidelineInstruction text="Click 'Edit' to add roles" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_edit_roles,
                        altext: "Edit roles page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="Add the following roles, and click save:" />
                  <Box sx={{ ml: 2, mb: 2 }}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="body2">
                        • <strong>rest_api_explorer</strong>
                      </Typography>
                      <CustomCopyButton
                        copyTitle="Role"
                        copyText="rest_api_explorer"
                      />
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="body2">
                        • <strong>rest_service</strong>
                      </Typography>
                      <CustomCopyButton
                        copyTitle="Role"
                        copyText="rest_service"
                      />
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="body2">
                        • <strong>sn_incident_read</strong>
                      </Typography>
                      <CustomCopyButton
                        copyTitle="Role"
                        copyText="sn_incident_read"
                      />
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="body2">
                        • <strong>sn_incident_write</strong>
                      </Typography>
                      <CustomCopyButton
                        copyTitle="Role"
                        copyText="sn_incident_write"
                      />
                    </Box>
                  </Box>

                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_add_roles,
                        altext: "Add roles page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      p: 1.5,
                      backgroundColor: colors.warning_lighter,
                      borderRadius: 1,
                      fontWeight: "bold",
                    }}
                  >
                    ⚠️ Important: Don't forget to click the "Update" button
                    after adding all roles!
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_user_update,
                        altext: "User update page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Set User Password">
                  <GuidelineInstruction text="Find the user in the users list and click on it" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_user_search,
                        altext: "User search page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="Click 'Set Password' button" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_user_set_password,
                        altext: "User set password page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="Click 'Generate' to create a secure password and click on the copy button - you'll need it for the integration" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_generate_password,
                        altext: "Generate password page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <GuidelineInstruction text="Save the generated password" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: servicenow_user_save_password,
                        altext: "User save password page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Connect with ServiceNow Credentials">
                  <Typography variant={"body2"} sx={{ mb: 2 }}>
                    Enter your ServiceNow instance details to connect. Use the
                    credentials from the user you just created.
                  </Typography>

                  <Typography variant={"body2"} sx={{ mb: 2 }}>
                    Your instance URL should be in the format:{" "}
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      component="span"
                    >
                      <strong>{"https://your-instance.service-now.com"}</strong>
                      <CustomCopyButton
                        copyTitle="URL format"
                        copyText="https://your-instance.service-now.com"
                      />
                    </Box>
                  </Typography>

                  <ServiceNowConnectForm
                    onSubmit={onSubmit}
                    guideMode={guideMode}
                    initialValues={lastAttemptedValues}
                  />
                </GuidelineStep>
              </GuidelineStepByStep>
            </Box>
          )
        }
      />
    </Box>
  );
};

export default ServiceNowIntegration;
